/**
 * Display-format helpers shared across the SPAs.
 *
 * Admin-only helpers live in `admin/src/lib/format.ts`, which re-exports some
 * of these for admin imports.
 */

import type {
  DrugCoverageEntry,
  EnrollmentStatus,
  M3pProjectionDrug,
  PreferredLanguage,
} from "../types/api";

export type BadgeTone = "green" | "amber" | "red" | "blue" | "purple" | "gray";

/**
 * English display labels for the stored `preferredLanguage` key, for the
 * English-only admin and member SPAs; the customer form localizes through
 * i18n keys. The stored value stays the canonical key.
 */
export const LANGUAGE_LABELS: Record<PreferredLanguage, string> = {
  english: "English",
  spanish: "Spanish",
  korean: "Korean",
  mandarin: "Mandarin",
};

export const statusMap: Record<EnrollmentStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: "Draft", tone: "gray" },
  new: { label: "New", tone: "blue" },
  pending_m3p_enrollment: { label: "Pending M3P Enrollment", tone: "amber" },
  pending_lis_enrollment: { label: "Pending LIS Enrollment", tone: "amber" },
  pending_pap_enrollment: {
    label: "Pending Pharmaceutical Assistance Programs Enrollment",
    tone: "amber",
  },
  need_more_information: { label: "Need more information", tone: "purple" },
  denied_m3p_enrollment: { label: "Denied M3P Enrollment", tone: "red" },
  denied_lis_enrollment: { label: "Denied LIS Enrollment", tone: "red" },
  denied_pap_enrollment: {
    label: "Denied Pharmaceutical Assistance Programs Enrollment",
    tone: "red",
  },
  member_withdrew: { label: "Member Withdrew Application", tone: "gray" },
  enrolled_m3p: { label: "Successfully Enrolled in M3P", tone: "green" },
  enrolled_lis: { label: "Successfully Enrolled in LIS", tone: "green" },
  enrolled_pap: {
    label: "Successfully Enrolled in Pharmaceutical Assistance Programs",
    tone: "green",
  },
};

/** "May 20, 2026" in en-US, or "—" when missing or invalid. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Date-only strings (`YYYY-MM-DD`, e.g. `dob`) carry no time zone, so the
  // spec parses them as UTC midnight. Formatting that instant in local time
  // shifts the calendar day back for any viewer west of UTC, so date-only input
  // formats in UTC to show the exact day the user entered. Full timestamps
  // carry an instant and format in local time.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(dateOnly ? { timeZone: "UTC" } : {}),
  });
}

/**
 * "August 1, 2026": the long-month sibling of {@link fmtDate}, with the same
 * time-zone convention (a date-only value in UTC, a timestamp in local time).
 * Null or invalid input returns "—".
 */
export function fmtLongDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    ...(dateOnly ? { timeZone: "UTC" } : {}),
  });
}

/** "May 20, 2:14 PM" */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${day}, ${time}`;
}

/**
 * Format a US E.164 number `+1XXXXXXXXXX` as `(XXX) XXX-XXXX` for display.
 *
 * Anything that doesn't match the strict shape (wrong length, no `+1`) is
 * returned as-is, so non-US numbers aren't mangled.
 */
export function formatUsPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (!m) return e164;
  return `(${m[1]!}) ${m[2]!}-${m[3]!}`;
}

/**
 * Live as-you-type formatter that shapes a US phone input's digits into `(XXX) XXX-XXXX`.
 *
 * It strips non-digits, drops the leading `1` off an 11-digit paste (the
 * `normalizePhone` country-code rule), and caps at 10 digits, ignoring extra
 * keystrokes like `maxLength`. It never leaves punctuation after the last
 * digit, so backspace always removes a digit instead of fighting a re-inserted
 * separator. It is idempotent on its own output, and a complete number
 * satisfies `makePhoneSchema` (which strips back to 10 digits) and the server's
 * `normalize_phone`.
 */
export function formatPhoneInput(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Render a postal address on one line as `street1 · street2 · City, ST ZIP`, dropping blank segments.
 *
 * `street2` is optional because some addresses, such as a pharmacy's, have no second line.
 */
export function formatAddress(addr: {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
}): string {
  const lines = [addr.street1];
  if (addr.street2) lines.push(addr.street2);
  const cityLine = [addr.city, addr.state].filter(Boolean).join(", ");
  const tail = [cityLine, addr.zip].filter(Boolean).join(" ");
  if (tail) lines.push(tail);
  return lines.filter(Boolean).join(" · ");
}

/** "$547" / "$18.50": whole dollars stay whole, cents render as cents. */
export function formatDollars(value: number): string {
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

/**
 * A dollar figure with thousands grouping ("$1,200", "$35.50"), unlike `formatDollars`.
 *
 * Whole-dollar amounts drop the cents, fractional amounts keep two, and null
 * (not computable) renders "—". `cents` keeps two digits on whole dollars too
 * ("$200.00"), for money columns that mix whole and fractional amounts.
 */
export function fmtUsd(dollars: number | null, cents = false): string {
  if (dollars == null) return "—";
  const whole = !cents && Number.isInteger(dollars);
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
}

/** A member-cost figure as one string plus whether it is a min–max range (the surface's caption gate). */
export interface CostRange {
  /** "$200" / "$100–$200" / the "—" glyph. */
  text: string;
  /** True only when both bounds render differently; the surface then shows its range caption. */
  ranged: boolean;
}

/**
 * The min–max money rule for every M3P member-cost figure.
 *
 * `max` is the price with the deductible unmet and `min` the price with it
 * already met, rendered "$min–$max" through the surface's own formatter, so
 * each surface's rounding decides whether the bounds collapse. The result is
 * the single `max` figure when the two bounds format alike (the server sends
 * `min == max` once year-to-date spending is known, and some drugs never touch
 * the deductible) or when `min` is absent, as it may be on stored checks. A
 * null `max` renders "—" whatever `min` says.
 */
export function costRange(
  min: number | null | undefined,
  max: number | null,
  fmt: (dollars: number) => string = fmtUsd,
): CostRange {
  if (max == null) return { text: fmtUsd(null), ranged: false };
  const high = fmt(max);
  const low = min == null ? high : fmt(min);
  return low === high ? { text: high, ranged: false } : { text: `${low}–${high}`, ranged: true };
}

/** The range caption in staff wording (admin, partner). A surface shows it only while a range is visible on it. */
export const COST_RANGE_CAPTION =
  "Low end assumes the deductible is already met; high end assumes none of it is.";
/** The same caption for member-facing surfaces (the picker, the review card, the dashboard tile, the picker's (i)). */
export const YOUR_COST_RANGE_CAPTION =
  "Low end assumes your deductible is already met; high end assumes none of it is.";

/**
 * The marker line wherever next year's estimated prices show (the medication
 * picker, the projection card): "2027 estimate. Drug prices may change in
 * January." The year is the payload's, never a literal.
 */
export function nextYearMarker(year: number): string {
  return `${year} estimate. Drug prices may change in January.`;
}

/**
 * The plan-year toggle's (i) popover: what a next-year estimate applies to
 * what. Every figure comes from the `GET /api/plans/years` payload: the two
 * years and next year's statutory out-of-pocket cap (the cap clause is dropped
 * when the payload carries none). Meaningless while `next` is null, where no
 * toggle renders.
 */
export function planYearPopover(years: {
  current: number;
  next: number | null;
  nextOopThreshold: number | null;
}): string {
  const cap =
    years.nextOopThreshold === null ? "" : `, ${fmtUsd(years.nextOopThreshold)} out-of-pocket cap`;
  return (
    `Medicare hasn't published ${years.next} drug prices yet. This estimate applies your plan's ` +
    `${years.next} rules (deductible, copays, coinsurance${cap}) to ${years.current} drug prices. ` +
    "Prices are published in January, so your numbers may change."
  );
}

/**
 * What a drug's member cost renders as.
 *
 * A null result renders as nothing (the picker) or as the surface's "—" (the
 * admin table). `range` is a "$min–$max" price, which gates the surface's
 * caption; `price` is a single figure.
 */
export type MemberCostDisplay =
  | { kind: "price"; amount: string }
  | { kind: "range"; amount: string }
  | { kind: "notCovered" };

/**
 * The member-cost render rule shared by the customer medication picker and every admin drug surface.
 *
 * Sharing it means the same plan and rxcui read the same number everywhere;
 * each SPA keeps its own markup. A priced projection row wins: its
 * `costMin`–`cost` (the member's out-of-pocket for one fill, deductible met to
 * unmet) as bare dollars via `costRange`, never with a unit suffix. A drug the
 * plan's formulary doesn't carry (`covered: false` on the coverage entry or
 * the row) reads "Not covered". Anything else (no row, no entry,
 * `coverageKnown: false`, or a covered row the calculator couldn't price) is
 * null rather than a wrong number.
 */
export function memberCostDisplay(
  row: M3pProjectionDrug | null | undefined,
  coverage: DrugCoverageEntry | null | undefined,
): MemberCostDisplay | null {
  if (row?.cost != null) {
    const { text, ranged } = costRange(row.costMin, row.cost);
    return ranged ? { kind: "range", amount: text } : { kind: "price", amount: text };
  }
  if (coverage?.covered === false || row?.covered === false) return { kind: "notCovered" };
  return null;
}
